import React, { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import DataTable from 'react-data-table-component';
import TransactionApiService from '../../ApiServices/TransactionApiService/TransactionApiService';
import InvoiceApiService from '../../ApiServices/InvoiceApiService/InvoiceApiService';
import { useAuth } from '../Auth/AuthContext';
import generateTaxInvoice from '../Invoice/GenerateTaxInvoice';
import generateReceipt from '../Invoice/GenerateReciept';
import PayInvoiceModal from '../transaction/PayInvoiceModal';
import { DataStyles } from '../Common/DataTableStyles';

const TransactionTable = () => {
    const [transactions, setTransactions] = useState([]);
    const [filteredTransactions, setFilteredTransactions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [isPayInvoice, setPayInvoice] = useState(false);
    const [currentItem, setCurrentItem] = useState(null);

    const { authState } = useAuth();
    const user = authState.user;
    const isAdmin = authState.roles === "Admin";

    const fetchTransactions = async () => {
        setLoading(true);
        try {
            const response = await TransactionApiService.GetAllTransactionByUserId(isAdmin ? null : user.id);
            setTransactions(response.data);
            console.log("setTransactions", response.data);
            setFilteredTransactions(response.data);
        } catch {
            toast.error('Failed to fetch transactions.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (user?.id) {
            fetchTransactions();
        }
    }, [user]);

    const handleSearch = (e) => {
        const value = e.target.value;
        setSearchTerm(value);
        const filtered = transactions.filter(transaction =>
            transaction.revenue.toString().includes(value) ||
            transaction.deductions.toString().includes(value) ||
            transaction.paymentType.toLowerCase().includes(value.toLowerCase()) ||
            (transaction.isPaid ? 'Paid' : 'Unpaid').toLowerCase().includes(value.toLowerCase())
        );
        setFilteredTransactions(filtered);
    };

    const fetchAndDownloadPDF = async (transactionId) => {
        try {
            const response = await TransactionApiService.GetById(transactionId);
            if (response.data?.deductionImage) {
                const base64 = response.data.deductionImage;
                const byteCharacters = atob(base64);
                const byteNumbers = Array.from(byteCharacters).map(char => char.charCodeAt(0));
                const byteArray = new Uint8Array(byteNumbers);
                const blob = new Blob([byteArray], { type: "application/pdf" });

                const link = document.createElement("a");
                link.href = URL.createObjectURL(blob);
                link.download = "DeductionReason.pdf";
                link.click();
            } else {
                toast.error("No PDF data available.");
            }
        } catch {
            toast.error("Failed to fetch PDF.");
        }
    };

    const handlePayInvoice = (row) => {
        setPayInvoice(true);
        setCurrentItem(row);
    };

    const handleDeleteInvoice = async (row) => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: 'This invoice will be permanently deleted!',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, delete it!'
        });

        if (result.isConfirmed) {
            try {
                await InvoiceApiService.Delete(row.id);
                toast.success("Invoice deleted successfully!");
                fetchTransactions();
            } catch {
                toast.error("Failed to delete invoice.");
            }
        }
    };

    const handlePayInvoiceSubmit = async (e) => {
        e.preventDefault();
        try {
            const formData = new FormData(e.target);
            formData.append("InvoiceId", currentItem.id);
            await TransactionApiService.PayInvoice(formData);
            setPayInvoice(false);
            fetchTransactions();
        } catch (error) {
            toast.error('Error updating item:', error.message);
        }
    };

    const DownloadInvoice = async (id) => {
        const invoiceInfo = await TransactionApiService.GetInvoiceInfo(id);
        if (invoiceInfo?.isSuccess && invoiceInfo?.data) {
            generateTaxInvoice(invoiceInfo.data);
        }
    };

    const DownloadReceipt = async (id) => {
        const invoiceInfo = await TransactionApiService.GetInvoiceInfo(id);
        if (invoiceInfo?.isSuccess && invoiceInfo?.data) {
            generateReceipt(invoiceInfo.data);
        }
    };

const columns = [
  ...(isAdmin ? [
    {
      name: 'Publisher',
      selector: row => row.user?.firstName + " " + row.user?.lastName || 'N/A',
      sortable: true,
      minWidth: '180px',
      wrap: true
    }
  ] : []),
  {
    name: 'Company',
    selector: row => row.user?.company || 'N/A',
    sortable: true,
    minWidth: '160px',
    wrap: true
  },
  {
    name: 'Web',
    selector: row => row.website?.name || 'N/A',
    sortable: true,
    minWidth: '200px',
    wrap: true
  },
  {
    name: 'Invoice Month',
    selector: row => row.previousMonth || 'N/A',
    sortable: true,
    minWidth: '140px'
  },
  {
    name: 'Revenue',
    selector: row => `$${row.revenue.toFixed(2)}`,
    sortable: true,
    right: true,
    minWidth: '120px'
  },
  {
    name: 'Deductions',
    selector: row => `$${row.deductions.toFixed(2)}`,
    sortable: true,
    right: true,
    minWidth: '130px'
  },
  {
    name: 'Payment Type',
    selector: row => row.paymentType,
    sortable: true,
    minWidth: '140px',
    wrap: true
  },
  {
    name: 'Status',
    selector: row => row.isPaid ? 'Paid' : 'Unpaid',
    cell: row => (
      <span className={`font-bold ${row.isPaid ? 'text-green-600' : 'text-red-500'}`}>
        {row.isPaid ? 'Paid' : 'Unpaid'}
      </span>
    ),
    sortable: true,
    minWidth: '100px'
  },
  {
    name: 'Actions',
    cell: row => (
      <div className="flex flex-wrap gap-2">
        {!row.isPaid && isAdmin && (
          <button onClick={() => handlePayInvoice(row)} className="bg-blue-500 text-white px-3 py-1 rounded hover:bg-blue-600 text-xs">
            Pay
          </button>
        )}
        <button onClick={() => DownloadInvoice(row.id)} className="bg-indigo-500 text-white px-3 py-1 rounded hover:bg-indigo-600 text-xs">
          Invoice
        </button>
        <button onClick={() => DownloadReceipt(row.id)} className="bg-green-500 text-white px-3 py-1 rounded hover:bg-green-600 text-xs">
          Receipt
        </button>
        {row.deductionImage && (
          <button onClick={() => fetchAndDownloadPDF(row.id)} className="bg-yellow-500 text-white px-3 py-1 rounded hover:bg-yellow-600 text-xs">
            PDF
          </button>
        )}
        {isAdmin && (
          <button onClick={() => handleDeleteInvoice(row)} className="bg-red-500 text-white px-3 py-1 rounded hover:bg-red-600 text-xs">
            Delete
          </button>
        )}
      </div>
    ),
    minWidth: '280px',
    wrap: true
  }
];


    return (
        <div className="p-6 bg-gray-100 min-h-screen">
            <ToastContainer />
            <div className="max-w-7xl mx-auto">
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-2xl font-bold">Transactions</h2>
                    <input
                        type="text"
                        className="w-64 border px-4 py-2 rounded-md shadow-sm focus:ring-2 focus:ring-indigo-500"
                        placeholder="Search..."
                        value={searchTerm}
                        onChange={handleSearch}
                    />
                </div>
                <div className="bg-white rounded-lg shadow-md">
                    <DataTable
                        columns={columns}
                        data={filteredTransactions}
                        progressPending={loading}
                        pagination
                        highlightOnHover
                        striped
                        customStyles={DataStyles}
                        noDataComponent="No transactions found."
                    />
                </div>
            </div>

            {isPayInvoice && (
                <PayInvoiceModal
                    item={currentItem}
                    onClose={() => setPayInvoice(false)}
                    onSubmit={handlePayInvoiceSubmit}
                />
            )}
        </div>
    );
};

export default TransactionTable;
